/**
 * SequencerService - Manages Tone.js transport and sequencer scheduling
 * Ported from js/sequencer.js with TypeScript types
 */

import * as Tone from 'tone';
import type { Voice } from './VoiceFactory';

/**
 * Callback function for step changes during playback
 */
export type StepChangeCallback = (step: number) => void;

/**
 * SequencerService class manages the Tone.js transport and scheduling
 */
export class SequencerService {
  private repeatId: number | null = null;
  private stepChangeCallback: StepChangeCallback | null = null;
  private loopEnabled: boolean = true;
  private steps: number = 16;

  /**
   * Initialize the sequencer with voices and BPM
   * 
   * @param voices - Array of voice trigger functions
   * @param gridState - 2D array of active cells [row][step]
   * @param noteState - 2D array of note pitches [row][step]
   * @param steps - Total number of steps in the pattern
   * @param bpm - Beats per minute
   * @param onStepChange - Callback function called when step changes
   */
  initSequencer(
    voices: Voice[],
    gridState: boolean[][],
    noteState: (string | null)[][],
    steps: number,
    bpm: number = 120,
    onStepChange?: StepChangeCallback
  ): void {
    // Set tempo
    Tone.Transport.bpm.value = bpm;
    
    // Store steps count
    this.steps = steps;

    // Store callback
    if (onStepChange) {
      this.stepChangeCallback = onStepChange;
    }

    // Clear any existing schedule
    if (this.repeatId !== null) {
      Tone.Transport.clear(this.repeatId);
      this.repeatId = null;
    }

    // Precompute a 1/16-note duration
    const sixteenth = Tone.Time('16n').toSeconds();

    // Schedule on every sixteenth note
    this.repeatId = Tone.Transport.scheduleRepeat((time) => {
      const ticksPer16th = Tone.Transport.PPQ / 4;
      const step = Math.floor(Tone.Transport.ticks / ticksPer16th) % steps;

      // Fire step change callback
      if (this.stepChangeCallback) {
        this.stepChangeCallback(step);
      }
      
      // If loop is disabled and we've completed one cycle, stop
      if (!this.loopEnabled && step === this.steps - 1) {
        Tone.Transport.stop();
      }

      // Trigger notes for each voice
      voices.forEach((voice, row) => {
        const prev = (step + steps - 1) % steps;
        
        // Check if row exists in grid state
        if (!gridState[row] || !noteState[row]) return;
        
        // Start only at the beginning of a run
        if (gridState[row][step] && !gridState[row][prev]) {
          // Measure run length (connected notes)
          let len = 1;
          while (gridState[row][(step + len) % steps] && len < steps) {
            len++;
          }
          
          // Duration = len × one sixteenth
          const duration = sixteenth * len;
          const note = noteState[row][step] ?? null;
          
          // Call the trigger function
          voice.trigger(time, note, duration);
        }
      });
    }, '16n');
  }

  /**
   * Update the BPM of the transport
   * 
   * @param bpm - New beats per minute value
   */
  setBpm(bpm: number): void {
    Tone.Transport.bpm.value = bpm;
  }

  /**
   * Start playback
   */
  async start(): Promise<void> {
    await Tone.start();
    Tone.Transport.start();
  }

  /**
   * Pause playback
   */
  pause(): void {
    Tone.Transport.pause();
  }

  /**
   * Stop playback and reset position
   */
  stop(): void {
    Tone.Transport.stop();
    Tone.Transport.position = 0; // Reset to beginning
  }

  /**
   * Set loop mode
   * 
   * @param enabled - Whether looping is enabled
   */
  setLoop(enabled: boolean): void {
    this.loopEnabled = enabled;
  }

  /**
   * Clear the sequencer schedule
   */
  clear(): void {
    if (this.repeatId !== null) {
      Tone.Transport.clear(this.repeatId);
      this.repeatId = null;
    }
  }

  /**
   * Get current transport state
   */
  getState(): 'started' | 'paused' | 'stopped' {
    return Tone.Transport.state;
  }

  /**
   * Dispose of the sequencer and clean up resources
   */
  dispose(): void {
    this.clear();
    this.stepChangeCallback = null;
  }
}
